import { ArrowUpRight, ArrowDownRight, Wallet, Landmark } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { rupiah, tanggal, bulanLabel } from "@/lib/format";
import { SectionTitle } from "./SectionTitle";

export const FinanceSection = ({ finance, transactions = [], profile, full = false }) => {
  const stats = [
    { key: "masuk", label: "Total Kas Masuk", value: finance?.total_masuk, icon: ArrowUpRight, tone: "text-emerald-700 bg-emerald-50" },
    { key: "keluar", label: "Total Kas Keluar", value: finance?.total_keluar, icon: ArrowDownRight, tone: "text-red-700 bg-red-50" },
    { key: "saldo", label: "Saldo Kas Saat Ini", value: finance?.saldo, icon: Wallet, tone: "text-[#a8871f] bg-[#f6ebd0]" },
  ];
  const chart = (finance?.monthly || []).map((m) => ({ ...m, label: bulanLabel(m.bulan) }));

  return (
    <section id="keuangan" className="scroll-mt-24" data-testid="finance-section">
      {!full && <SectionTitle eyebrow="Transparansi Keuangan" title="Ringkasan Kas Masjid" desc="Setiap rupiah infaq jamaah dicatat dan dilaporkan secara terbuka sebagai bentuk amanah pengurus DKM. Berikut saldo terkini, arus kas 6 bulan terakhir, dan transaksi terbaru." more="/keuangan" moreTestId="finance-see-all" />}
      <div className={`${full ? "" : "mt-10"} grid md:grid-cols-3 gap-5`}>
        {stats.map((s) => (
          <div key={s.key} className="card-hover rounded-3xl bg-white border border-emerald-900/10 p-6" data-testid={`finance-${s.key}-card`}>
            <div className={`h-10 w-10 rounded-2xl flex items-center justify-center ${s.tone}`}>
              <s.icon size={18} />
            </div>
            <p className="mt-5 text-sm text-slate-500">{s.label}</p>
            <p className="mt-1 text-2xl font-extrabold text-emerald-950 tracking-tight" data-testid={`finance-${s.key}-value`}>{rupiah(s.value)}</p>
          </div>
        ))}
      </div>
      <div className="mt-6 grid lg:grid-cols-5 gap-5">
        <div className="lg:col-span-3 rounded-3xl bg-white border border-emerald-900/10 p-6" data-testid="finance-chart">
          <h3 className="font-semibold text-emerald-950">Arus Kas 6 Bulan Terakhir</h3>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chart} barGap={4}>
                <CartesianGrid vertical={false} stroke="#e8ede8" />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#52635a" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#52635a" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${Math.round(v / 1000)}k`} width={44} />
                <Tooltip formatter={(v) => rupiah(v)} cursor={{ fill: "#f3f6f1" }} />
                <Bar dataKey="masuk" name="Kas Masuk" fill="#145c48" radius={[6, 6, 0, 0]} />
                <Bar dataKey="keluar" name="Kas Keluar" fill="#d4af37" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="lg:col-span-2 rounded-3xl emerald-bg pattern-overlay text-emerald-50 p-6 flex flex-col" data-testid="finance-recent">
          <h3 className="font-semibold">Transaksi Terbaru</h3>
          <ul className="mt-4 space-y-3 flex-1">
            {transactions.slice(0, 5).map((t) => (
              <li key={t.id} className="flex items-center justify-between gap-3 text-sm border-b border-white/10 pb-3 last:border-0">
                <div>
                  <p className="font-medium">{t.category}</p>
                  <p className="text-xs text-emerald-300/80">{tanggal(t.date)}</p>
                </div>
                <span className={`font-bold ${t.type === "masuk" ? "text-[#80b996]" : "text-[#f6ebd0]"}`}>
                  {t.type === "masuk" ? "+" : "-"}{rupiah(t.amount)}
                </span>
              </li>
            ))}
            {transactions.length === 0 && <li className="text-sm text-emerald-300">Belum ada transaksi.</li>}
          </ul>
          {profile?.bank_account && (
            <div className="mt-5 rounded-2xl bg-black/20 p-4 text-sm" data-testid="finance-bank-info">
              <p className="flex items-center gap-2 text-[#d4af37] font-semibold"><Landmark size={14} /> Rekening Infaq</p>
              <p className="mt-1 font-mono text-lg">{profile.bank_account}</p>
              <p className="text-xs text-emerald-200">{profile.bank_name} a.n. {profile.bank_holder}</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
